import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import { colors, strCode } from "../../../consts";
import { notificationService, t } from "../../../services";
import { objectUtil } from "../../../utils";

import {
  DropdownInput,
  FlexContainers,
  Input,
  Modal,
  SmallButton,
  TextButton,
  WrapperContainer
} from "../../../components";

const NewMercuryHardwareModal = ({
  hardwareData,
  locations,
  loading,
  active,
  setActive,
  onSave
}) => {
  const [macAddress, setMacAddress] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedMarket, setSelectedMarket] = useState(null);

  const [isValid, setIsValid] = useState(false);
  const [isSameData, setIsSameData] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [missingFields, setMissingFields] = useState({});

  const [isSubmitted, setIsSubmitted] = useState(false);

  const markets = useSelector((state) => state.markets?.markets);

  const allLocationOptions = useMemo(() => {
    return locations.map((el) => {
      return { id: el.id, value: el?.locationName };
    });
  }, [locations]);

  const marketLocationOptions = useMemo(() => {
    const market = selectedMarket?.id ?? null;

    return market
      ? locations
          .filter((el) => el.market === market)
          .map((el) => {
            return { id: el.id, value: el?.locationName };
          })
      : [];
  }, [locations, selectedMarket]);

  const marketOptions = useMemo(() => {
    return markets.map((market) => {
      return {
        id: market.market,
        value: market.market
      };
    });
  }, [markets.length]);

  const getNewMercuryHardwareData = useCallback(() => {
    let newData = {
      routerId: macAddress,
      market: selectedMarket?.id ?? null,
      locationId: selectedLocation?.id ?? null
    };

    return newData;
  }, [macAddress, selectedMarket, selectedLocation]);

  useEffect(() => {
    prepopulateData();

    return () => {
      resetFormData();
    };
  }, [hardwareData]);

  useEffect(() => {
    let valid = !!macAddress?.length;

    const errorFields = {
      macAddress: !macAddress?.length
    };

    setMissingFields(errorFields);

    setIsValid(valid);

    if (hardwareData) {
      const newData = getNewMercuryHardwareData();

      const originalData = { ...hardwareData };
      delete originalData.hubId;

      setIsSameData(objectUtil.objEquals(originalData, newData));
    }
  }, [macAddress, selectedMarket, selectedLocation]);

  useEffect(() => {
    if (isSubmitted && !loading && active) {
      onClose();
    }
  }, [isSubmitted, loading, active]);

  useEffect(() => {
    setSelectedLocation(
      (prev) => marketLocationOptions?.find((location) => location.id === prev?.id) ?? null
    );
  }, [selectedMarket, marketLocationOptions]);

  const prepopulateData = useCallback(() => {
    if (hardwareData) {
      setMacAddress(hardwareData?.routerId);
      setSelectedMarket(marketOptions.find((m) => m.id === hardwareData?.market));

      const locationId = hardwareData?.locationId ?? null;

      if (locationId) {
        setSelectedLocation(allLocationOptions.find((location) => location.id === locationId));
      }
    }
  }, [hardwareData]);

  const resetFormData = () => {
    setMacAddress("");
    setSelectedMarket(null);
    setSelectedLocation(null);

    setIsValid(true);
    setIsSameData(false);
    setShowErrors(false);
    setMissingFields({});

    setIsSubmitted(false);
  };

  const resetMarket = () => {
    setSelectedMarket(null);
    setSelectedLocation(null);
  };

  const handleSave = async () => {
    if (!showErrors) {
      setShowErrors(true);
    }

    if (!isValid) {
      notificationService.showError({
        title: t(strCode.MISSING_FIELDS),
        message: t(strCode.MISSING_FIELDS_MESSAGE)
      });
      return;
    }

    const data = getNewMercuryHardwareData();

    if (hardwareData?.routerId) {
      data.routerIds = [data.routerId];
    }

    setIsSubmitted(true);
    await onSave(data);
    resetFormData();
  };

  const onClose = () => {
    setActive(false);
    resetFormData();
  };

  return (
    <Modal
      id={"mercury-hardware-modal"}
      active={active}
      closable={false}
      title={t(hardwareData?.routerId ? hardwareData?.routerId : strCode.NEW_ROUTER_TITLE)}
      preventOverflow>
      <WrapperContainer Container={FlexContainers.FlexColumnStart} gap={24} width={382}>
        <Input
          id={"mac-address"}
          title={t(strCode.MAC_ADDRESS_TITLE)}
          placeholder={t(strCode.MAC_ADDRESS_PLACEHOLDER)}
          value={macAddress}
          setValue={setMacAddress}
          hasError={showErrors && missingFields?.macAddress}
          disabled={loading || !!hardwareData?.routerId}
          isRequired
        />

        <DropdownInput
          id="markets-selector"
          title={t(strCode.MARKET_TITLE)}
          placeholder={t(strCode.SELECT_PLACEHOLDER_2)}
          options={marketOptions}
          selectedOption={selectedMarket}
          onSelectOption={(id) => {
            setSelectedMarket(marketOptions.find((el) => el.id === id));
          }}
          hasError={showErrors && missingFields?.market}
          isSearchable
        />

        <DropdownInput
          id="hardware-location-selector"
          title={t(strCode.ASSIGNED_LOCATION_TITLE)}
          placeholder={t(strCode.CHOOSE_LOCATION_PLACEHOLDER)}
          options={marketLocationOptions}
          selectedOption={selectedLocation}
          onSelectOption={(id) => {
            setSelectedLocation(marketLocationOptions.find((el) => el.id === id));
          }}
          hasError={showErrors && missingFields?.location}
          disabled={!selectedMarket}
          isSearchable
        />

        <WrapperContainer
          Container={
            hardwareData?.routerId ? FlexContainers.FlexRowBetween : FlexContainers.FlexRowEnd
          }
          gap={8}
          width={"100%"}>
          {!!hardwareData?.routerId && (
            <TextButton
              color={colors.ERROR}
              onClick={resetMarket}
              disabled={loading || !selectedMarket}>
              {t(strCode.UNASSIGN_BTN)}
            </TextButton>
          )}

          <WrapperContainer Container={FlexContainers.FlexRowStart} gap={8}>
            <SmallButton secondary onClick={onClose} disabled={loading}>
              {t(strCode.CANCEL_BTN)}
            </SmallButton>
            <SmallButton onClick={handleSave} disabled={isSameData || loading} loading={loading}>
              {t(strCode.SAVE_BTN)}
            </SmallButton>
          </WrapperContainer>
        </WrapperContainer>
      </WrapperContainer>
    </Modal>
  );
};

NewMercuryHardwareModal.propTypes = {
  hardwareData: PropTypes.shape({
    routerId: PropTypes.string.isRequired,
    market: PropTypes.string,
    locationId: PropTypes.string
  }),
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      locationName: PropTypes.string
    })
  ).isRequired,
  loading: PropTypes.bool.isRequired,
  active: PropTypes.bool.isRequired,
  setActive: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired
};

export default NewMercuryHardwareModal;
